/* eslint-disable */
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Coffee, ChefHat, UtensilsCrossed, User, CheckCircle, Circle } from 'lucide-react';

// ── Colours ───────────────────────────────────────────────────────────
export const C = {
  yellow:'#F7EC71', yellowLight:'#FEFCE8', orange:'#E4AC49', orangeLight:'#FDF3DC',
  green:'#7EA594', greenLight:'#EAF2EF', cream:'#F7F6EF', charcoal:'#2F3A42',
  navy:'#445366', mid:'#7A8A96', border:'#D8DEE3', skyBlue:'#BCD9E5',
  skyBlueLight:'#EAF4F8', skyText:'#3A8FA3', white:'#FFFFFF',
  red:'#DC3545', redLight:'#FFF0F0', amber:'#E07B00', amberLight:'#FFF3E0',
  purple:'#7C3AED', purpleLight:'#F3EEFF',
};

// ── Constants ─────────────────────────────────────────────────────────
export const VENUES         = ['Box Jordaan', 'Box Plantage', 'Sassys', 'Prep Kitchen'];
export const DEPTS          = ['FOH', 'KIT', 'Management'];
export const CONTRACT_TYPES = ['Fixed Term', 'Permanent'];
export const EMP_TYPES      = ['Full Time', 'Part Time'];
export const CONTRACT_HRS   = [32, 36, 38, 40];
export const BAR_SKILLS     = ['Barista', 'Bar', 'Service', 'Host', 'Supervisor', 'Manager'];
export const KIT_SKILLS     = ['KP', 'Commis Chef', 'CDP', 'Sous Chef', 'Head Chef'];
export const MONTHS         = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
export const UNIFORM_DEPOSIT = { KIT: 200, default: 15 };

export const emptyForm = {
  name:'', venue:'Box Jordaan', dept:'FOH', role:'',
  employment_type:'Full Time', contract_type:'Fixed Term',
  contract_months:'', contract_hours:38,
  join_date:'', contract_start_date:'', contract_end_date:'',
  contract_number:1, skills:[], probation_done:false,
  mid_checkin_done:false, contract_renewal:false,
  birthday:'', active:true, shifts_per_week:'', email:'',
  uniform_deposit_paid: false,
  contract_signed: false,
  nmbrs_profile_done: false,
  street:'', zip:'', city:'', bsn:'', iban:'', phone:'', nationality:'',
  id_doc_path:'', bsn_doc_path:'',
  eitje_user_id:'', leave_accrual_pct:8.0, balance_calc_start_date:'',
  opening_ot_balance:0, opening_leave_balance:0,
};

// ── Date helpers ──────────────────────────────────────────────────────
export function addMonths(ds, m) {
  if (!ds||!m) return null;
  const d=new Date(ds); d.setMonth(d.getMonth()+parseInt(m));
  return d.toISOString().split('T')[0];
}
export function fmt(ds) {
  if (!ds) return '—';
  return new Date(ds).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'});
}
export function fmtDT(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'});
}
export function daysUntil(ds) {
  if (!ds) return null;
  const t=new Date(); t.setHours(0,0,0,0);
  const d=new Date(ds); d.setHours(0,0,0,0);
  return Math.floor((d-t)/86400000);
}
export function daysSince(ds) {
  if (!ds) return null;
  const t=new Date(); t.setHours(0,0,0,0);
  const d=new Date(ds); d.setHours(0,0,0,0);
  return Math.floor((t-d)/86400000);
}
export function midDate(s,e,ct) {
  if (ct==='Permanent') {
    if (!s) return null;
    let n=new Date(s); const now=new Date();
    while (n<=now) n.setMonth(n.getMonth()+6);
    return n.toISOString().split('T')[0];
  }
  if (!s||!e) return null;
  const sv=new Date(s).getTime(), ev=new Date(e).getTime();
  return new Date(sv+(ev-sv)/2).toISOString().split('T')[0];
}
export function isNew(m) {
  const d=daysSince(m.join_date||m.contract_start_date);
  return d!==null&&d>=0&&d<=30;
}
export function pulseAlert(m) {
  const md=midDate(m.contract_start_date,m.contract_end_date,m.contract_type);
  if (!md) return null;
  const d=daysUntil(md);
  if (d!==null&&d>=-7&&d<=14&&!m.mid_checkin_done)
    return {days:d,label:d<0?'Overdue':`${d}d`,urgency:d<0?'high':'medium'};
  return null;
}
export function contractAlert(m) {
  if (m.contract_type!=='Fixed Term'||!m.contract_end_date) return null;
  const d=daysUntil(m.contract_end_date);
  if (d!==null&&d>=0&&d<=42) return {days:d,label:`${d}d`,urgency:d<=14?'high':'medium'};
  return null;
}
export function genToken() {
  return Math.random().toString(36).substring(2,10)+Math.random().toString(36).substring(2,10);
}
export function depositAmount(dept) {
  return dept === 'KIT' ? UNIFORM_DEPOSIT.KIT : UNIFORM_DEPOSIT.default;
}

// ── Styles ────────────────────────────────────────────────────────────
export const pill=(active,sm=false)=>({
  padding:sm?'4px 10px':'6px 14px', borderRadius:'20px',
  border:`1px solid ${active?C.navy:C.border}`, cursor:'pointer',
  fontSize:sm?'12px':'13px', fontWeight:active?600:400,
  background:active?C.navy:C.white, color:active?C.white:C.mid,
});
export const inp={width:'100%',padding:'8px 10px',border:`1px solid ${C.border}`,borderRadius:'8px',fontSize:'13px',color:C.charcoal,background:C.white,outline:'none',boxSizing:'border-box'};
export const lbl={fontSize:'12px',fontWeight:600,color:C.charcoal,display:'block',marginBottom:'4px'};

// ── useIsMobile hook ──────────────────────────────────────────────────
export function useIsMobile(bp=768) {
  const [m,setM]=useState(()=>window.innerWidth<bp);
  useEffect(()=>{
    const h=()=>setM(window.innerWidth<bp);
    window.addEventListener('resize',h);
    return()=>window.removeEventListener('resize',h);
  },[bp]);
  return m;
}

// ── RoleIcon ──────────────────────────────────────────────────────────
export function RoleIcon({dept,size=32}) {
  const map={FOH:{bg:C.skyBlueLight,c:C.skyText},KIT:{bg:C.orangeLight,c:C.amber},Management:{bg:C.purpleLight,c:C.purple}};
  const s=map[dept]||{bg:C.cream,c:C.mid}; const sz=size*0.44;
  return (
    <div style={{width:size,height:size,borderRadius:'50%',background:s.bg,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,border:`1px solid ${s.c}22`}}>
      {dept==='FOH'&&<Coffee size={sz} strokeWidth={1.8} color={s.c}/>}
      {dept==='KIT'&&<ChefHat size={sz} strokeWidth={1.8} color={s.c}/>}
      {dept==='Management'&&<UtensilsCrossed size={sz} strokeWidth={1.8} color={s.c}/>}
      {!['FOH','KIT','Management'].includes(dept)&&<User size={sz} strokeWidth={1.8} color={s.c}/>}
    </div>
  );
}

// ── BalancePill ───────────────────────────────────────────────────────
export function BalancePill({value,type}) {
  if (value===null||value===undefined||value==='') return <span style={{color:C.border}}>—</span>;
  const n=parseFloat(value);
  let c=C.charcoal,bg=C.cream;
  if (type==='ot'){if(n>8){c=C.red;bg=C.redLight;}else if(n>0){c=C.amber;bg=C.amberLight;}else{c=C.green;bg=C.greenLight;}}
  if (type==='leave'){if(n<8){c=C.red;bg=C.redLight;}else if(n<16){c=C.amber;bg=C.amberLight;}else{c=C.green;bg=C.greenLight;}}
  return <span style={{background:bg,color:c,padding:'2px 8px',borderRadius:'6px',fontSize:'13px',fontWeight:700}}>{type==='ot'&&n>0?`+${n}`:n}h</span>;
}

// ── MemberDocButton ───────────────────────────────────────────────────
export function MemberDocButton({label, path}) {
  function open() {
    if (!path) return;
    const { data } = supabase.storage.from('new-hire-docs').getPublicUrl(path);
    if (data?.publicUrl) window.open(data.publicUrl, '_blank');
  }
  return (
    <button onClick={open}
      style={{display:'flex',alignItems:'center',gap:'6px',padding:'6px 12px',background:C.skyBlueLight,border:`1px solid ${C.skyBlue}44`,borderRadius:'7px',cursor:'pointer',fontSize:'12px',fontWeight:600,color:C.skyText}}>
      {label}
    </button>
  );
}

// ── ChecklistRow ──────────────────────────────────────────────────────
export function ChecklistRow({ item, canEdit, saving, toggleItem, updateNotes, saveNotes, showArea }) {
  return (
    <div style={{display:'flex',alignItems:'flex-start',gap:'10px',padding:'8px 10px',background:item.done?C.greenLight:C.white,borderRadius:'8px',border:`1px solid ${item.done?C.green+'33':C.border}`,transition:'all 0.15s'}}>
      <button onClick={()=>toggleItem(item.id)} disabled={!canEdit||!!saving[item.id]}
        style={{background:'none',border:'none',cursor:canEdit?'pointer':'default',padding:'1px',flexShrink:0,marginTop:'1px'}}>
        {item.done ? <CheckCircle size={18} color={C.green}/> : <Circle size={18} color={C.border}/>}
      </button>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:'flex',alignItems:'center',gap:'6px',flexWrap:'wrap'}}>
          <span style={{fontSize:'13px',color:item.done?C.green:C.charcoal,fontWeight:item.done?600:400,textDecoration:item.done?'line-through':'none'}}>{item.description}</span>
          {showArea && <span style={{fontSize:'10px',fontWeight:600,color:C.mid,background:C.cream,padding:'1px 6px',borderRadius:'20px',flexShrink:0}}>{item.area}</span>}
          {!showArea && <span style={{fontSize:'10px',fontWeight:600,color:C.mid,background:C.cream,padding:'1px 6px',borderRadius:'20px',flexShrink:0}}>{item.timeline}</span>}
        </div>
        <input
          value={item.notes||''}
          onChange={e=>updateNotes(item.id,e.target.value)}
          onBlur={e=>saveNotes(item.id,e.target.value)}
          placeholder="Notes..."
          disabled={!canEdit}
          style={{marginTop:'4px',width:'100%',padding:'3px 7px',border:`1px solid ${C.border}`,borderRadius:'5px',fontSize:'11px',color:C.mid,background:'transparent',outline:'none',boxSizing:'border-box'}}
        />
      </div>
      {item.done&&(
        <div style={{textAlign:'right',flexShrink:0}}>
          <div style={{fontSize:'10px',fontWeight:600,color:C.green}}>{item.manager||'—'}</div>
          <div style={{fontSize:'10px',color:C.mid}}>{item.date||''}</div>
        </div>
      )}
    </div>
  );
}